import { useEffect, useState, useRef } from "react";
import { FaFileExcel } from "react-icons/fa";
import React from "react";
import { API_ROUTES } from "../../api/routes";
import {
    fetchEmpresasMonitor,
    fetchClientesMonitor,
    fetchMesesMonitor,
    fetchDetalleMonitor,
    fetchDetallePrecioLlamada
} from "../../api/reportes";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import loadingGif from "../../assets/Loading.gif"
import { DateTime } from 'luxon';

export default function Monitor() {
    const [empresas, setEmpresas] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [meses, setMeses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filaExpandida, setFilaExpandida] = useState(null);
    const [empresaSeleccionada, setEmpresaSeleccionada] = useState("");
    const [clienteSeleccionado, setClienteSeleccionado] = useState("");
    const [tablaSeleccionada, setTablaSeleccionada] = useState("");
    const [datos, setDatos] = useState([]);
    const [pagina, setPagina] = useState(0);
    const [totalRegistros, setTotalRegistros] = useState(0);
    const registrosPorPagina = 10;
    const [detalleFila, setDetalleFila] = useState({});
    const [terminoBusqueda, setTerminoBusqueda] = useState("");
    const [fechaInicio, setFechaInicio] = useState("");
    const [fechaFinal, setFechaFinal] = useState("");
    const [horaInicio, setHoraInicio] = useState("");
    const [horaFin, setHoraFin] = useState("");
    const [exportando, setExportando] = useState(false);
    const [rangoMes, setRangoMes] = useState({ min: "", max: "" });

    // helper para YYYYMM -> {min,max}
    const parseYYYYMM = (yyyymm) => {
        const s = String(yyyymm || "");
        if (!/^\d{6}$/.test(s)) return null;
        const y = Number(s.slice(0, 4));
        const m = Number(s.slice(4, 6)); // 1..12
        if (m < 1 || m > 12) return null;
        const mm = String(m).padStart(2, "0");
        const min = `${y}-${mm}-01`;
        const last = new Date(y, m, 0).getDate(); // último día del mes
        const max = `${y}-${mm}-${String(last).padStart(2, "0")}`;
        return { y, m, min, max };
    };

    const extractYYYYMM = (value) => {
        const m = String(value || "").match(/(\d{6})$/);
        return m ? m[1] : null; // "202508" aunque el valor sea "contactacc202508"
    };

    useEffect(() => {
        fetchEmpresasMonitor().then(setEmpresas);
        fetchMesesMonitor().then(setMeses);
    }, []);

    useEffect(() => {
        if (fechaInicio && (!fechaFinal || fechaFinal === fechaAnterior.current)) {
            setFechaFinal(fechaInicio);
        }
        fechaAnterior.current = fechaInicio;
    }, [fechaInicio]);

    const fechaAnterior = useRef("");


    useEffect(() => {
        if (empresaSeleccionada) {
            fetchClientesMonitor(empresaSeleccionada).then(setClientes);
            setClienteSeleccionado("");
        }
    }, [empresaSeleccionada]);

    useEffect(() => {
        const yyyymm = extractYYYYMM(tablaSeleccionada);
        const info = parseYYYYMM(yyyymm);
        if (info) {
            setRangoMes({ min: info.min, max: info.max });
            setFechaInicio((prev) =>
                prev && prev >= info.min && prev <= info.max ? prev : info.min
            );
            setFechaFinal((prev) =>
                prev && prev >= info.min && prev <= info.max ? prev : info.max
            );
        } else {
            setRangoMes({ min: "", max: "" });
            setFechaInicio("");
            setFechaFinal("");
        }
    }, [tablaSeleccionada]);



    const cargarDetalle = async (offset = 0) => {
        setLoading(true);
        const response = await fetchDetalleMonitor({
            table: tablaSeleccionada,
            client_id: clienteSeleccionado,
            start: offset,
            length: registrosPorPagina,
            search: terminoBusqueda,
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFinal,
            hora_inicio: horaInicio,
            hora_fin: horaFin,
            draw: 1,
            order: JSON.stringify([{ column: 1, dir: "desc" }]),
        });
        setDatos(response.data);
        setTotalRegistros(response.recordsFiltered);
        setLoading(false);
    };



    const cambiarPagina = (nuevaPagina) => {
        setPagina(nuevaPagina);
        cargarDetalle(nuevaPagina * registrosPorPagina);
    };

    const exportarAExcel = () => {
        if (datos.length === 0) return;

        setExportando(true);

        // Simulación de proceso pesado, si necesitas delay visible
        setTimeout(() => {
            const worksheet = XLSX.utils.json_to_sheet(datos);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "Monitor");
            const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
            const blob = new Blob([excelBuffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            saveAs(blob, `Monitor_${new Date().toISOString().slice(0, 10)}.xlsx`);
            setExportando(false);
        }, 200); // puedes quitar el setTimeout si no es necesario
    };

    function generarRangoPaginador(paginaActual, totalPaginas) {
        const delta = 2;
        const range = [];
        const left = Math.max(2, paginaActual - delta);
        const right = Math.min(totalPaginas - 1, paginaActual + delta);

        range.push(1);
        if (left > 2) range.push("...");

        for (let i = left; i <= right; i++) {
            range.push(i);
        }

        if (right < totalPaginas - 1) range.push("...");
        if (totalPaginas > 1) range.push(totalPaginas);

        return range;
    }

    const handleExpandirFila = async (index, item) => {
        if (filaExpandida === index) {
            setFilaExpandida(null);
            return;
        }

        try {
            const fechaConvertida = DateTime.fromISO(item.AcctStartTime, { zone: 'utc' })
                .setZone('America/Mexico_City') // Ajusta según tu zona local
                .toFormat('yyyy-MM-dd HH:mm:ss');

            const detalle = await fetchDetallePrecioLlamada({
                acctSessionId: item.AcctSessionId,
                acctStartTime: fechaConvertida, // ← Ya en formato correcto
                mes: tablaSeleccionada.replace("contactacc", ""),
            });

            if (detalle) {
                setDetalleFila((prev) => ({ ...prev, [index]: detalle }));
                setFilaExpandida(index);
            }
        } catch (error) {
            console.error("Error al obtener el detalle de la llamada:", error);
        }
    };

    const descargarTodoCSV = async () => {
        if (!clienteSeleccionado || !tablaSeleccionada) return;

        setExportando(true);

        try {
            const params = new URLSearchParams({
                table: tablaSeleccionada,
                client_id: clienteSeleccionado,
                search: terminoBusqueda,
            });
            const response = await fetch(`${API_ROUTES.monitorDescargarCompleto}?${params.toString()}`, {
                credentials: 'include',
            });

            const datosCompletos = await response.json();

            if (!Array.isArray(datosCompletos) || datosCompletos.length === 0) {
                throw new Error("La respuesta del servidor no contiene datos válidos.");
            }

            // Generar encabezados desde la primera fila
            const encabezados = Object.keys(datosCompletos[0]);

            // Generar filas
            const filas = datosCompletos.map(row =>
                encabezados.map(key =>
                    `"${(row[key] ?? '').toString().replace(/"/g, '""')}"`
                ).join(',')
            );

            const contenidoCSV = [encabezados.join(','), ...filas].join('\n');

            const blob = new Blob([contenidoCSV], { type: "text/csv;charset=utf-8;" });
            saveAs(blob, `MonitorCompleto_${new Date().toISOString().slice(0, 10)}.csv`);
        } catch (e) {
            console.error("Error al descargar todo:", e);
        } finally {
            setExportando(false);
        }
    };

    return (
        <div className="bg-white p-6 rounded-xl shadow-lg">
            <div className="flex justify-between items-center mb-6 flex-wrap gap-2">
                <h2 className="text-lg font-bold text-gray-700">Monitor</h2>

                <div className="flex gap-2">
                    {/* Descargar Excel de la página actual */}
                    <button
                        className="flex items-center gap-1 border px-3 py-1 rounded text-sm text-green-600 hover:bg-green-50 disabled:opacity-50"
                        onClick={exportarAExcel}
                        disabled={datos.length === 0 || exportando}
                    >
                        <FaFileExcel />
                        {exportando ? "Generando..." : "Descargar Excel (página)"}
                    </button>

                    {/* Descargar Excel de todos los registros */}
                    <button
                        className="flex items-center gap-1 border px-3 py-1 rounded text-sm text-green-700 hover:bg-green-100 disabled:opacity-50"
                        onClick={descargarTodoCSV}
                        disabled={exportando || !clienteSeleccionado || !tablaSeleccionada}
                    >
                        <FaFileExcel />
                        {exportando ? "Descargando..." : "Descargar todo"}
                    </button>
                </div>
            </div>


            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm mb-4">
                {/* Empresa */}
                <div>
                    <label className="block mb-1 text-gray-600 font-medium">Empresa</label>
                    <select
                        value={empresaSeleccionada}
                        onChange={(e) => setEmpresaSeleccionada(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                        <option value="">- Empresa -</option>
                        {empresas.map((e) => (
                            <option key={e.id} value={e.id}>{e.text}</option>
                        ))}
                    </select>
                </div>

                {/* Cliente */}
                <div>
                    <label className="block mb-1 text-gray-600 font-medium">Cliente</label>
                    <select
                        value={clienteSeleccionado}
                        onChange={(e) => setClienteSeleccionado(e.target.value)}
                        disabled={!empresaSeleccionada}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                        <option value="">- Cliente -</option>
                        {clientes.map((c) => (
                            <option key={c.id} value={c.id}>{c.text}</option>
                        ))}
                    </select>
                </div>

                {/* Mes */}
                <div>
                    <label className="block mb-1 text-gray-600 font-medium">Mes</label>
                    <select
                        value={tablaSeleccionada}
                        onChange={(e) => setTablaSeleccionada(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                        <option value="">- Mes -</option>
                        {meses.map((m) => (
                            <option key={m.id} value={m.id}>{m.text}</option>
                        ))}
                    </select>
                </div>

                {/* Fecha específica */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Fecha inicio */}
                    <div>
                        <label className="block mb-1 text-gray-600 font-medium">Fecha inicio</label>
                        <input
                            type="date"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            value={fechaInicio || ""}
                            min={rangoMes.min || undefined}
                            max={rangoMes.max || undefined}
                            onChange={(e) => {
                                let v = e.target.value;
                                if (rangoMes.min && v < rangoMes.min) v = rangoMes.min;
                                if (rangoMes.max && v > rangoMes.max) v = rangoMes.max;
                                setFechaInicio(v);
                            }}
                        />

                    </div>

                    {/* Fecha final */}
                    <div>
                        <label className="block mb-1 text-gray-600 font-medium">Fecha final</label>
                        <input
                            type="date"
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-cyan-500"
                            value={fechaFinal || ""}
                            min={rangoMes.min || undefined}
                            max={rangoMes.max || undefined}
                            onChange={(e) => {
                                let v = e.target.value;
                                if (rangoMes.min && v < rangoMes.min) v = rangoMes.min;
                                if (rangoMes.max && v > rangoMes.max) v = rangoMes.max;
                                setFechaFinal(v);
                            }}
                        />
                    </div>
                </div>


                {/* Consultar (ocupando lugar del troncal) */}
                <div className="flex items-end">
                    <button
                        onClick={() => cargarDetalle(0)}
                        disabled={!clienteSeleccionado || !tablaSeleccionada || loading}
                        className={`w-full px-3 py-1 rounded text-white text-sm shadow 
                ${loading ? "bg-gray-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"}`}
                    >
                        {loading ? "Consultando..." : "Consultar"}
                    </button>
                </div>
            </div>

            {/* Línea separada para Número / Troncal y Horas */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm mb-6">
                {/* Número o troncal */}
                <div>
                    <label className="block mb-1 text-gray-600 font-medium">Número / Troncal</label>
                    <input
                        type="text"
                        placeholder="Ej: 5222... o +5255..."
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        value={terminoBusqueda}
                        onChange={(e) => setTerminoBusqueda(e.target.value)}
                    />
                </div>

                {/* Hora inicio */}
                <div>
                    <label className="block mb-1 text-gray-600 font-medium">Hora inicio</label>
                    <input
                        type="time"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        value={horaInicio}
                        onChange={(e) => setHoraInicio(e.target.value)}
                    />
                </div>

                {/* Hora fin */}
                <div>
                    <label className="block mb-1 text-gray-600 font-medium">Hora fin</label>
                    <input
                        type="time"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2"
                        value={horaFin}
                        onChange={(e) => setHoraFin(e.target.value)}
                    />
                </div>
            </div>

            {(loading || exportando) && (
                <div className="flex items-center gap-2 mb-4">
                    <img src={loadingGif} alt="Cargando..." className="w-5 h-5" />
                    <span className="text-sm text-gray-600">
                        {loading ? "Consultando datos..." : "Generando Excel..."}
                    </span>
                </div>
            )}



            {datos.length > 0 && (
                <div className="w-full overflow-x-auto mt-6">
                    <table className="w-full text-sm rounded shadow">
                        <thead className="sticky top-0 bg-gray-50 border-b z-10">
                            <tr className="text-gray-600 text-xs uppercase tracking-wide">
                                <th className="px-5 py-3 text-left">Fecha</th>
                                <th className="px-5 py-3 text-left">Troncal</th>
                                <th className="px-5 py-3 text-left">Teléfono</th>
                                <th className="px-5 py-3 text-left">Minutos</th>
                                <th className="px-5 py-3 text-left">Tipo</th>
                            </tr>
                        </thead>
                        <tbody>
                            {datos.map((item, idx) => {
                                return (
                                    <React.Fragment key={idx}>
                                        <tr
                                            onClick={() => handleExpandirFila(idx, item)}
                                            className=" hover:bg-cyan-50 cursor-pointer"
                                        >
                                            <td className="p-2">{new Date(item.CallDate).toLocaleString()}</td>
                                            <td className="p-2">{item.Troncal}</td>
                                            <td className="p-2">{item.Telefono}</td>
                                            <td className="p-2">{item.Minutos}</td>
                                            <td className="p-2">{item.Tipo}</td>
                                            {/* <td className="p-2">{item.SipApplicationType}</td> */}
                                        </tr>

                                        {filaExpandida === idx && (
                                            <tr className="bg-gray-50">
                                                <td colSpan={8} className="px-6 py-4">
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700">
                                                        <div className="space-y-2">
                                                            <p><span className="font-semibold text-gray-600">AcctStartTime:</span> {item.AcctStartTime}</p>
                                                            <p><span className="font-semibold text-gray-600">AcctStopTime:</span> {item.AcctStopTime}</p>
                                                            {/* {console.log("Detalle fila:", detalleFila[idx])} */}
                                                            <p>
                                                                <span className="font-semibold text-gray-600">Price:</span>{" "}
                                                                {filaExpandida === idx && detalleFila[idx]?.precio != null
                                                                    ? detalleFila[idx].precio.toFixed(3)
                                                                    : item.Price ?? "-"}
                                                            </p>
                                                            <p><span className="font-semibold text-gray-600">AcctTerminateCause:</span> {item.AcctTerminateCause}</p>
                                                        </div>
                                                        <div className="space-y-2">
                                                            <p><span className="font-semibold text-gray-600">SipTranslatedRequestURI:</span> {item.SipTranslatedRequestURI}</p>
                                                            <p><span className="font-semibold text-gray-600">SipApplicationType:</span> {item.SipApplicationType}</p>
                                                            <p><span className="font-semibold text-gray-600">SipCodecs:</span> {item.SipCodecs}</p>
                                                            <p><span className="font-semibold text-gray-600">CanonicalURI:</span> {item.CanonicalURI}</p>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>

                    {/* Información y navegación de paginación */}
                    {totalRegistros > 0 && (
                        <div className="flex flex-col md:flex-row justify-between items-center mt-4 text-sm gap-4">
                            <p className="text-gray-600">
                                Registros del {Intl.NumberFormat().format(pagina * registrosPorPagina + 1)} al{" "}
                                {Intl.NumberFormat().format(Math.min((pagina + 1) * registrosPorPagina, totalRegistros))} de un total de{" "}
                                {Intl.NumberFormat().format(totalRegistros)} registros
                            </p>

                            <div className="flex flex-wrap gap-1 items-center">
                                <button
                                    onClick={() => cambiarPagina(pagina - 1)}
                                    disabled={pagina === 0}
                                    className="px-3 py-1 border rounded disabled:opacity-50"
                                >
                                    Anterior
                                </button>

                                {generarRangoPaginador(pagina + 1, Math.ceil(totalRegistros / registrosPorPagina)).map((p, index) =>
                                    p === "..." ? (
                                        <span key={`ellipsis-${index}`} className="px-3 py-1 text-gray-500">
                                            ...
                                        </span>
                                    ) : (
                                        <button
                                            key={`page-${p}`}
                                            onClick={() => cambiarPagina(p - 1)}
                                            className={`px-3 py-1 border rounded ${p === pagina + 1 ? "bg-cyan-600 text-white" : "hover:bg-gray-100"}`}
                                        >
                                            {Intl.NumberFormat().format(p)}
                                        </button>
                                    )
                                )}

                                <button
                                    onClick={() => cambiarPagina(pagina + 1)}
                                    disabled={(pagina + 1) * registrosPorPagina >= totalRegistros}
                                    className="px-3 py-1 border rounded disabled:opacity-50"
                                >
                                    Siguiente
                                </button>
                            </div>
                        </div>
                    )}

                </div>
            )}
        </div>
    );
}
